import "./share.scss";
import { ComponentRegistry } from '../util/ComponentRegistry';
import { dom, icon } from "@fortawesome/fontawesome-svg-core";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";

type ShareHook = () => void | Promise<void>;
type ShareFailHook = (error: unknown) => void | Promise<void>;
type ShareOptions = {
    onClick?: ShareHook;
    onSuccess?: ShareHook;
    onFail?: ShareFailHook;
    onCancel?: ShareHook;
    url?: string;
    text?: string;
    title?: string;
    fetchUrl?: boolean;
    culture?: string;
    shareType?: number | string;

};
type webShareApiOptions = {
    title?: string;
    text?: string;
    url?: string;
}

export class Share {
    private readonly id: string;
    private static registry = new ComponentRegistry<Share>('share');
    private readonly trigger: HTMLButtonElement | HTMLAnchorElement;
    
    private _config = {
        supportsWebShareApi: false,
        isMobile: false,
        successDelay: 1000,
        shareBtnClass: 'share-btn'
    };

    private _shareTypes = {
        webShareApi: 0,
        clipboard: 1,
        sms: 2
    }

    private constructor(
        trigger: HTMLButtonElement | HTMLAnchorElement,
        private options: ShareOptions
    ) {
        this.id = Share.registry.register(trigger, this);
        this.trigger = trigger;
        
        this.webShareApi = Boolean(typeof navigator.share !== "undefined");
        this.isMobileDevice = Boolean(this.deviceType() !== "desktop");
        
        this.trigger.classList.add(this._config.shareBtnClass);
        this.setClickEvent();
        
        if (!this.webShareApi) {
            this.setCopyButtonLabel();
        }
    }

    private setClickEvent() {
        this.trigger.addEventListener('click', async (e) => {
            e.preventDefault();
            
            if (typeof this.options.onClick === 'function') {
                await this.options.onClick();
            }

            try {
                await this.performShare();  
                if (typeof this.options.onSuccess === 'function') {
                    await this.options.onSuccess();
                }
                this.setSuccessBtn();
            } catch (err: unknown) {
                const name = (err as DOMException)?.name ?? (err as any)?.name;
                if (name === 'AbortError') {
                    await this.options.onCancel?.();
                    console.log('Share cancelled:', err);
                } else {
                    if (typeof this.options.onFail === 'function') {
                        await this.options.onFail(err);
                    }
                    console.log('Share failed:', err);
                }
            }
        });
    }

    private setSuccessBtn() {
        this.trigger.classList.add('share-success');

        const svgIcon = this.trigger.querySelector('svg');
        if (!svgIcon) return;
        
        const originalSvgClone = svgIcon.cloneNode(true) as SVGElement;

        const checkSvg = this.createIconSvg(faCircleCheck);
        svgIcon.replaceWith(checkSvg);

        window.setTimeout(() => {
            this.trigger.classList.remove('share-success');
            const currentSvg = this.trigger.querySelector('svg');
            if (currentSvg) {
                currentSvg.replaceWith(originalSvgClone);
            }  
        }, this._config.successDelay);
    }

    private createIconSvg(iconDefinition: any): SVGElement {
        const iconMarkup = icon(iconDefinition);
        const svgHtml = iconMarkup.html[0] ?? '';
        const fragment = document.createRange().createContextualFragment(svgHtml);
        const svg = fragment.firstElementChild as SVGElement;
        svg.setAttribute('aria-hidden', 'true');
        return svg;
    }

    private async setCopyButtonLabel() {
        const buttonLabel = this.trigger.querySelector('.btn-label');
        const label = this.trigger.dataset?.copyLabel;
        await dom.i2svg({ node: this.trigger });
    
        if (label && buttonLabel) {
            const svgIcon = this.trigger.querySelector('svg');
            buttonLabel.textContent = label;
            if (svgIcon) svgIcon.setAttribute('data-icon', 'copy');
        }
    }


    private async fetchShareUrl(): Promise<string | undefined> {
        const {
            url,
            fetchUrl,
            culture,
            shareType
        } = this.options;

        if (!fetchUrl) { return url; }

        if (!url) {
            throw new Error('fetchUrl is true but no endpoint URL was provided');
        }

        const params = new URLSearchParams();
        if (culture) params.append('culture', culture);
        if (shareType !== undefined) params.append('shareType', String(shareType));

        const response = await fetch(
            `${url}?${params.toString()}`,
            { 
                credentials: 'same-origin',
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            }
        );

        if (!response.ok) {
            throw new Error(`Failed to fetch share URL (${response.status})`);
        }

        const data = await response.json();
        this.options.title = data.content || this.options.title;

        return data.url;
    }

    private async performShare() {
        const data: webShareApiOptions = {};

        // decide shareType
        if (this.webShareApi) {
            this.options.shareType = this.shareType.webShareApi;
        } else if (this.isMobileDevice) {
            this.options.shareType = this.shareType.sms;
        } else {
            this.options.shareType = this.shareType.clipboard;
        }

        const shareUrl = await this.fetchShareUrl();
        const cleanedText = this.cleanShareText(this.options.text ?? '');

        if (cleanedText) {
            data.text = cleanedText;
        }
        else {
            if (this.options.title) {
                data.title = this.options.title;
            }
            if (shareUrl) {
                data.url = shareUrl;
            }
        }

        if (this.webShareApi) {
            console.log('share payload:', data);
            await navigator.share(data);
            return;
        }

        // clipboard fallback prefers text, then url
        const clipboardText = data.text ?? data.url ?? '';
        await this.copyToClipboard(clipboardText);
    }

    private async copyToClipboard(text: string) {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
        } else {
            const ta = document.createElement('textarea');
            ta.value = text;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
        }
    }

    get shareType() {
        return this._shareTypes;
    }

    get isMobileDevice(): boolean { return this._config.isMobile; }
    set isMobileDevice(status: boolean) { this._config.isMobile = status; }

    get webShareApi(): boolean { return this._config.supportsWebShareApi; }
    set webShareApi(status: boolean) { this._config.supportsWebShareApi = status; }

    private deviceType () {
        const ua = navigator.userAgent;
        if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
            return "tablet";
        }
        else if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
            return "mobile";
        }
        return "desktop";
    }

    private cleanShareText (text: string) {
        if (!text) return '';
        const cleanedText = text.replace(/\u00A0/g, ' ')
            .replace(/\r\n/g, '\n')
            .replace(/[ \t]+/g, ' ')
            .replace(/\n{2,}/g, '\n')
            .trim();
        return cleanedText;
    }

    public static create(
        target: string | HTMLElement | NodeListOf<HTMLElement> | HTMLElement[],
        options: ShareOptions = {}
    ): Share[] | null {

        const triggers = ComponentRegistry.processTargets<Share, HTMLElement>(
            Share.registry,
            target,
            [HTMLButtonElement, HTMLAnchorElement]
        ) as (HTMLButtonElement | HTMLAnchorElement)[];

        if (!triggers.length) return null;
        
        return triggers.map(trigger => new Share(trigger, options));
    }

    public static getShare(id: string): Share | null {
        return Share.registry.get(id) ?? null;
    }

    public destroy(): void {
        Share.registry.unregister(this.id);
    }
}